// apps/hr/src/pages/hr/MonthlyReport.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Download,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const MonthlyReport = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMonthlyReport();
  }, [month]);

  const fetchMonthlyReport = async () => {
    try {
      setLoading(true);
      // Get employees
      const employees = await pb.collection('employees').getFullList({
        filter: 'status = "active"',
      });
      const totalEmployees = employees.length;

      // Get salaries for month
      const salaries = await pb.collection('salary').getFullList({
        filter: `month = "${month}"`,
      });
      const totalSalary = salaries.reduce((sum, s) => sum + (s.amount || 0), 0);

      // Get attendance for month
      const attendance = await pb.collection('attendance').getFullList({
        filter: `date >= "${month}-01" && date <= "${month}-31"`,
      });
      const present = attendance.filter(a => a.status === 'present').length;
      const absent = attendance.filter(a => a.status === 'absent').length;
      const onLeave = attendance.filter(a => a.status === 'leave').length;

      setReport({
        month,
        totalEmployees,
        totalSalary,
        present,
        absent,
        onLeave,
        attendanceRate: totalEmployees > 0 ? Math.round((present / (totalEmployees * 30)) * 100) : 0,
      });
    } catch (error) {
      console.error('Error fetching monthly report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Monthly Report</h1>
          <p className="text-sm text-slate-500">Detailed monthly HR report</p>
        </div>
        <div className="flex gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          />
          <button className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition flex items-center gap-2">
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {report && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Total Employees</p>
              <p className="text-2xl font-bold text-slate-800">{report.totalEmployees}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Total Salary</p>
              <p className="text-2xl font-bold text-slate-800">₹{report.totalSalary.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Present</p>
              <p className="text-2xl font-bold text-green-600">{report.present}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Attendance Rate</p>
              <p className="text-2xl font-bold text-indigo-600">{report.attendanceRate}%</p>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Attendance Breakdown</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Present</span>
                  <span className="font-medium text-slate-800">{report.present} days</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${(report.present / (report.present + report.absent + report.onLeave)) * 100 || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Absent</span>
                  <span className="font-medium text-slate-800">{report.absent} days</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full" style={{ width: `${(report.absent / (report.present + report.absent + report.onLeave)) * 100 || 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">On Leave</span>
                  <span className="font-medium text-slate-800">{report.onLeave} days</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(report.onLeave / (report.present + report.absent + report.onLeave)) * 100 || 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyReport;